import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyModule } from '@ngx-formly/core';
import {
  CourseContentActions,
  CourseSection,
  CoursesContent,
  ResourcesLists,
  selectActiveSectionID,
  selectActiveSubSectionID,
  selectActiveSubsection,
  selectCompletedActivity,
  selectCourseContent,
} from '@shared';
import { NavigationComponent } from '@shared/components/navigation';
import { ProgressCircleComponent } from '@shared/components/progress';
import { MyClassService, TranscriptService } from '@shared/services';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Observable, combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, tap } from 'rxjs';
import {
  AvatarComponent,
  CourseFilterPipe,
  LoaderService,
  LocalStorage,
  Logger,
  RouterStoreService,
  ToastService,
} from 'thkee-common';
const log = new Logger('LearninglabSidebarComponent');

@UntilDestroy()
@Component({
  selector: 'app-learninglab-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    NgStyle,
    AngularSvgIconModule,
    FormsModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    FormlyModule,
    ProgressCircleComponent,
    AvatarComponent,
    NavigationComponent,
    AsyncPipe,
    RouterLink,
    CourseFilterPipe,
  ],
})
export class LearninglabSidebarComponent implements OnInit {
  searchText: string = '';

  sectionExpandedMap: Record<string, boolean> = {};
  params: any = {};
  sectionsData: CourseSection[] = [];
  courseDetail$!: Observable<CoursesContent>;
  activeSectionId$!: Observable<string>;
  activeSubsectionId$!: Observable<string>;
  completedActivity$!: Observable<{ [sectionOrSubsectionId: string]: boolean }>;
  isLoading$ = this.loaderService.response('learning-lab-sidebar');

  // isPreview!: string;
  // isFrom!: string;
  queryParams: { preview: boolean; from: string } = {
    preview: false,
    from: '',
  };
  isLoading: boolean = true;
  videoPosition = 3;
  isTranscript: boolean = false;

  constructor(
    private routerStore: RouterStoreService,
    private store: Store,
    private myClassService: MyClassService,
    private router: Router,
    private loaderService: LoaderService,
    private readonly toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private transcriptService: TranscriptService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.courseDetail$ = this.store
      .select(selectCourseContent)
      .pipe(filter((courseDetail) => (courseDetail.results?.length ?? 0) > 0));

    this.activeSectionId$ = this.store.select(selectActiveSectionID).pipe(
      distinctUntilChanged(),
      tap((activeSectionId) => {
        return (this.sectionExpandedMap[activeSectionId] = true);
      })
    );

    this.activeSubsectionId$ = this.store.select(selectActiveSubSectionID);

    this.completedActivity$ = this.store.select(selectCompletedActivity);

    this.courseDetail$.pipe(untilDestroyed(this)).subscribe((courseDetail) => {
      this.sectionsData = courseDetail.results ?? [];
      this.sectionsData.forEach((section) => {
        this.sectionExpandedMap[section.chain_id] = true;
      });
    });

    combineLatest([this.routerStore.getParams(), this.courseDetail$])
      .pipe(untilDestroyed(this))
      .subscribe(([params]) => {
        // this.store.dispatch(
        //   LearningLabActions.selectActivity({
        //     sectionId: params['section'],
        //     subsectionId: params['subsection'],
        //   })
        // );
        this.params = params;
      });

    // this.isPreview = this.activatedRoute.snapshot.queryParams['preview'];
    // this.isFrom = this.activatedRoute.snapshot.queryParams['from'];

    firstValueFrom(this.activatedRoute.queryParams).then((params) => {
      this.queryParams = params as { preview: boolean; from: string };
    });

    // Transcript
    this.transcriptService.getVideoPosition$.pipe(debounceTime(4), untilDestroyed(this)).subscribe((data) => {
      this.videoPosition = data;
    });

    this.transcriptService.isShowTranscript$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.isTranscript = data;
      this.getTranscript();
    });
  }

  marked(data: any, event: any, section_chain_id: string) {
    event.stopPropagation();
    const isPreview = this.activatedRoute.snapshot.queryParams['preview'];
    const isCompleted: boolean = data.is_completed ? false : true;
    let body = {
      // position: 5,
      subsection: data.id,
      is_completed: isCompleted,
      watched_duration: 0,
    };
    if (isPreview === 'true') {
      this.store.dispatch(
        CourseContentActions.previewCoursesUpdate({
          subsectionUpdate: { chain_id: data.chain_id, is_completed: isCompleted },
          nestedSubsectionUpdate: {
            subsection: data.id,
            is_completed: isCompleted,
            watched_duration: 0,
            position: data.position,
            section_chain_id: section_chain_id,
            subsection_chain_id: data.chain_id,
          },
        })
      );
      this.toastService.message({
        message: $localize`You can mark lectures as completed to see how it looks, but changes won’t be saved!`,
      });
      return;
    }
    if (!isPreview) {
      this.myClassService.markedAsRead(body, this.params['overviewcode']).subscribe({
        next: (res) => {
          this.store.dispatch(CourseContentActions.markedAsReadSuccess(res));
          this.store.dispatch(CourseContentActions.loadCourseCertificate({ product_id: this.params['courseId'] }));
        },
        error: (err) => {
          this.toastService.error({
            message: $localize`Failed to marked as unread!`,
          });
        },
      });
    }
  }

  selectActivity(sectionId: string, subsectionId: string, data: any, event: Event) {
    event.stopPropagation();
    this.updateRoute(sectionId, subsectionId);
    this.store.dispatch(
      CourseContentActions.selectActivity({
        sectionId,
        subsectionId,
      })
    );
  }

  // selectSection(sectionId: string) {
  //   this.store.dispatch(
  //     LearningLabActions.selectSection({
  //       sectionId,
  //       subsectionId: this.sectionsData?.find((s) => s.id === sectionId)?.subsections[0].id ?? '',
  //     })
  //   );
  // }

  // selectSubsection(subsectionId: string) {
  //   this.store.dispatch(LearningLabActions.selectSubsection({ subsectionId }));
  // }

  // NOTE: Not using now, but should be required latter
  // getUrl(sectionId: string, subsectionId: string = '', event: Event) {
  //   event.stopPropagation();
  //   const section = this.sectionsData?.find((s) => s.id === sectionId);
  //   subsectionId = subsectionId ?? section?.subsections[0]?.id;
  //   this.updateRoute(sectionId, subsectionId);
  // }

  // NOTE: Not using now, but should be required latter
  // getLink(sectionId: string, subsectionId: string = '') {
  //   let url = ['/learning-lab', this.params.courseId];
  //   if (this.params.tab) {
  //     url.push(this.params.tab);
  //   }
  //   if (sectionId) {
  //     url.push(sectionId);
  //   }
  //   if (subsectionId) {
  //     url.push(subsectionId);
  //   }
  //   if (this.params.overviewcode) {
  //     url.push(this.params.overviewcode);
  //   }

  //   return url;
  // }

  updateRoute(sectionId: string, subsectionId: string) {
    this.saveCurrentVideoLocalStorage(subsectionId);
    if (this.queryParams.preview) {
      this.router.navigate(
        ['learning-lab', this.params.courseId, this.params.tab, sectionId, subsectionId, this.params.overviewcode],
        {
          queryParams: this.queryParams,
          replaceUrl: true,
        }
      );
    } else {
      this.router.navigate([
        'learning-lab',
        this.params.courseId,
        this.params.tab,
        sectionId,
        subsectionId,
        this.params.overviewcode,
      ]);
    }
  }

  saveCurrentVideoLocalStorage(subsectionId: string) {
    const subsection = LocalStorage.getText('current_video');
    const current_video_position = LocalStorage.getText('current_video_position');
    if (subsection !== subsectionId) {
      this.saveVideoPosition(parseInt(current_video_position ?? '0'));
      setTimeout(() => {
        LocalStorage.setText('current_video', subsectionId);
      }, 1000);
    }
  }

  saveVideoPosition(duration: number) {
    firstValueFrom(this.store.select(selectActiveSubsection)).then((subs) => {
      if (this.params['overviewcode'] && subs?.type === 'lecture' && subs.lecture?.type === 'video') {
        this.myClassService
          .markedAsRead({ subsection: subs?.id ?? '', watched_duration: duration }, this.params['overviewcode'])
          .pipe(distinctUntilChanged(), untilDestroyed(this))
          .subscribe({
            next: (res) => {},
            error: ({ error }) => {
              this.toastService.error({
                message: error.error,
              });
            },
          });
      }
    });
  }

  // Resources
  showResource: boolean[] = [];
  onClickResource(resources: ResourcesLists[], id: number, event: Event) {
    this.showResource[id] = !this.showResource[id];
    event.stopPropagation();
  }

  isCompleted(section: CoursesContent) {
    let hasuncompleted = section.subsections.filter(
      (sec) => sec.is_completed === false || sec.is_completed === null
    ).length;
    return hasuncompleted > 0 ? false : true;
  }

  // Duration convert :hr,min,sec
  formatDuration(duration: number): string {
    const totalSeconds = Math.round(duration);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    if (totalSeconds < 60) {
      return `${seconds}sec`;
    } else if (hours > 0) {
      return `${hours}hr ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  }

  hideDropdown(id: number) {
    this.showResource[id] = false;
  }

  openPdf(file_url: string, label: string, event: any) {
    event.stopPropagation();
    // window.open(file_url, '_blank');
    this.downloadFile(file_url, label);
  }

  // Download direct
  downloadFile(url: string, filename: string = 'test') {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename || 'download';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((error) => console.error('Download failed:', error));
  }

  // Helper function to determine file extension from MIME type
  private getFileExtension(mimeType: string): string {
    const mimeMap: { [key: string]: string } = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'text/csv': 'csv',
      'application/zip': 'zip',
    };
    return mimeMap[mimeType] || 'bin'; // Default to .bin if unknown
  }

  public link: string = 'https://www.gstatic.com/webp/gallery/2.jpg';

  // Demo transcript
  transcriptType: string = '';
  transcript: { text: string; timestamp: Array<number> }[] = [];

  async getTranscript() {
    const transcript = await firstValueFrom(this.transcriptService.getSelectedTranscript$);
    this.transcriptType = transcript.label;
    if (transcript.src) {
      fetch(transcript.src)
        .then((response) => response.json())
        .then((data) => {
          this.transcript = data.chunks;
        })
        .catch((error) => console.error('Error fetching transcript:', error));
    }
  }

  hideTranscript() {
    this.isTranscript = false;
  }

  setCurrentPosition(timestamp: Array<number>) {
    this.transcriptService.setVideoPosition(this.parseTimestamp(timestamp).start || 0);
    const parsedTime = this.parseTimestamp(timestamp);
    if (parsedTime && parsedTime.start !== undefined) {
      this.transcriptService.setVideoSelectedPosition(parsedTime.start);
    }
  }

  addBreak(content: string) {
    if (content.trim().endsWith('.')) {
      return true;
    }
    return false;
  }

  parseTimestamp(timestamp: Array<number>): { start: number; end: number } {
    const [start, end] = timestamp.map((time) => time);
    return { start, end };
  }

  isSelected(timestamp: Array<number>): boolean {
    const { start, end } = this.parseTimestamp(timestamp);
    return this.videoPosition >= start && this.videoPosition < end;
  }
}
